import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface ConsumptionContextType {
  currentResidenceId: string;
  setCurrentResidenceId: (id: string) => void;
  currentUnit: {
    customerId: string;
    block: string;
    name: string;
    meterTypes: string[];
    commercialServiceName?: string;
    residenceName?: string;
  } | null;
  canAccessWater: boolean;
  canAccessGas: boolean;
  canAccessEnergy: boolean;
  beginningOfPeriod: string;
  setBeginningOfPeriod: (period: string) => void;
}

const ConsumptionContext = createContext<ConsumptionContextType | undefined>(undefined);

export function ConsumptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [currentResidenceId, setCurrentResidenceIdState] = useState<string>('');
  const [beginningOfPeriod, setBeginningOfPeriod] = useState<string>('');

  // Inicializa com a primeira unidade do usuário
  useEffect(() => {
    if (user?.residences && user.residences.length > 0 && !currentResidenceId) {
      setCurrentResidenceIdState(user.residences[0].customerId);
    }
  }, [user, currentResidenceId]);

  // Função para atualizar o ID da unidade selecionada
  const setCurrentResidenceId = (id: string) => {
    setCurrentResidenceIdState(id);
    // Também podemos salvar no localStorage para persistir entre sessões
    localStorage.setItem('current_residence_id', id);
  };

  // Encontra a unidade atual
  const currentUnit = user?.residences?.find(u => u.customerId === currentResidenceId) || null;

  // Calcula permissões de acesso baseadas nos tipos de medidor da unidade
  const canAccessWater = currentUnit?.meterTypes?.some(
    t => t === 'AGUA-FRIA' || t === 'AGUA-QUENTE'
  ) || false;
  
  const canAccessGas = currentUnit?.meterTypes?.includes('GAS') || false;
  
  const canAccessEnergy = currentUnit?.meterTypes?.includes('ENERGIA') || false;

  return (
    <ConsumptionContext.Provider
      value={{
        currentResidenceId,
        setCurrentResidenceId,
        currentUnit,
        canAccessWater,
        canAccessGas,
        canAccessEnergy,
        beginningOfPeriod,
        setBeginningOfPeriod,
      }}
    >
      {children}
    </ConsumptionContext.Provider>
  );
}

export function useConsumption() {
  const context = useContext(ConsumptionContext);
  if (context === undefined) {
    throw new Error('useConsumption must be used within a ConsumptionProvider');
  }
  return context;
}
